import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  CheckCircle, 
  Mail, 
  Calendar, 
  FileText, 
  Download,
  ArrowRight,
  Home,
  RefreshCw
} from 'lucide-react';

const AdmissionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const applicationNumber = searchParams.get('applicationNumber');

  const handleCheckStatus = () => {
    navigate(`/admission/status?applicationNumber=${applicationNumber}`);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-2xl mx-auto px-6">
        <Card className="text-center">
          <CardHeader>
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-600">Application Submitted Successfully!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <p className="text-gray-700 text-lg">
                Thank you for submitting your admission application to Bridge of Life Academy.
              </p>
              
              {applicationNumber && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Your Application Number:</p>
                  <Badge variant="outline" className="text-lg font-mono">
                    {applicationNumber}
                  </Badge>
                </div>
              )}
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-semibold text-yellow-800 mb-2">What happens next?</h3>
                <ul className="text-sm text-yellow-700 space-y-1 text-left">
                  <li>• You will receive a confirmation email shortly</li>
                  <li>• Your application will be reviewed by our admission committee</li>
                  <li>• We will contact you within 5-7 business days</li>
                  <li>• If selected, you may be invited for an interview</li>
                </ul>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Important Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-500" />
                  <div className="text-left">
                    <p className="text-sm font-medium">Email Confirmation</p>
                    <p className="text-xs text-gray-600">Check your inbox</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-green-500" />
                  <div className="text-left">
                    <p className="text-sm font-medium">Review Process</p>
                    <p className="text-xs text-gray-600">5-7 business days</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-500" />
                  <div className="text-left">
                    <p className="text-sm font-medium">Keep Documents</p>
                    <p className="text-xs text-gray-600">For reference</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Next Steps</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-semibold text-blue-600">1</span>
                    </div>
                    <span className="text-sm">Check your email for confirmation</span>
                  </div>
                  <Badge variant="outline" className="text-xs">Pending</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-semibold text-gray-600">2</span>
                    </div>
                    <span className="text-sm">Application review by committee</span>
                  </div>
                  <Badge variant="outline" className="text-xs">Upcoming</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-semibold text-gray-600">3</span>
                    </div>
                    <span className="text-sm">Interview invitation (if applicable)</span>
                  </div>
                  <Badge variant="outline" className="text-xs">Upcoming</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-semibold text-gray-600">4</span>
                    </div>
                    <span className="text-sm">Final admission decision</span>
                  </div>
                  <Badge variant="outline" className="text-xs">Upcoming</Badge>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <Button onClick={handleCheckStatus} className="flex-1">
                <RefreshCw className="w-4 h-4 mr-2" />
                Check Application Status
              </Button>
              <Button variant="outline" onClick={handleGoHome} className="flex-1">
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm text-gray-600">
                Need help? Contact our admission office at{' '}
                <a href="mailto:Info@bridgeoflifeacademy" className="text-blue-600 hover:underline">
                  Info@bridgeoflifeacademy
                </a>{' '}
                or call{' '}
                <a href="tel:+2348023025695" className="text-blue-600 hover:underline">
                  +234 8023025695
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdmissionSuccess;
